import React, { useMemo } from 'react';
import {
	StylovyzeTableBody,
	StylovyzeTableRow,
	getRole,
} from '@innovyze/stylovyze';

import { addRouteNamespace } from '@Utils/actions';
import { getValue } from '@Selectors';
import { RoleList, subscriptionsList } from '@Types/entitlements.types';
import { useSelector } from 'react-redux';
import { useGlobalization } from '@Translations';

export interface EntitlementsTableBodyProps {
	rowCount: number;
}

export default function EntitlementsTableBody({
	rowCount,
}: EntitlementsTableBodyProps): React.ReactElement {
	const { t } = useGlobalization();
	const { users: users } = useSelector(getValue);
	const {
		paginatedUsers: { users: rows, initialized },
	} = users;
	const userTableRows: StylovyzeTableRow[] = useMemo(
		() =>
			rows.map(({ id, name, role, username, entitlements }) => {
				const roleCell =
					RoleList(t).find(el => el.key == getRole(role))?.value ||
					'';
				const entitlementsCell = entitlements
					.map(
						entitlement =>
							subscriptionsList(t).find(
								el => el.key == entitlement,
							)?.value || entitlement,
					)
					.sort()
					.reduce((acc, el) => `${acc}${el}, `, '')
					.slice(0, -2);

				return {
					key: id,
					cells: [
						name,
						roleCell,
						username,
						{ content: entitlementsCell, ellipsis: true },
					],
					pathToGoWhenClicked: addRouteNamespace(`update-user/${id}`),
					ellipsis: true,
				};
			}),
		[rows],
	);
	return (
		<StylovyzeTableBody
			headers={[
				{ key: 'name', cell: t('Name'), width: 'auto', align: 'left' },
				{
					key: 'role',
					cell: t('Role'),
					width: '200px',
					align: 'left',
					sortable: false,
				},
				{
					key: 'username',
					cell: t('Username'),
					width: 'auto',
					align: 'left',
				},
				{
					key: 'subscriptions',
					cell: t('Subscriptions'),
					width: 'auto',
					maxWidth: '200px',
					align: 'left',
				},
			]}
			initialized={initialized}
			useSkeleton={{
				rowCount,
			}}
			rows={userTableRows}
			dataCy={'subscriptions-table-body'}
		/>
	);
}
